import axios from '@utils/http'

export const state = {
  currentUser: getSavedState('auth.currentUser'),
  roles: ['admin'],
}

export const mutations = {
  SET_CURRENT_USER(state, newValue) {
    if (newValue && !newValue.name) {
      newValue.name = `${newValue.firstName} ${newValue.lastName}`
    }
    state.currentUser = newValue
    saveState('auth.currentUser', newValue)
    setDefaultAuthHeaders(state)
  },
}

function permissionHelper(state, permissionName) {
  if (state.currentUser && state.currentUser.isAdmin) {
    return true
  }

  if (
    state.currentUser &&
    state.currentUser.permissions &&
    state.currentUser.permissions.length &&
    state.currentUser.permissions[0][permissionName]
  ) {
    return true
  }

  if (state.currentUser && state.currentUser.userRoles) {
    var allPermissions = state.currentUser.userRoles.map(
      ur => ur.role.permissions
    )
    for (var i = 0; i < allPermissions.length; i++) {
      var permissions = allPermissions[i]
      if (permissions[permissionName]) {
        return true
      }
    }
  }

  return false
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentUser
  },
  currentUserIsAdmin(state) {
    return state.currentUser && state.currentUser.isAdmin
  },
  currentUserCanImportFiles(state) {
    return permissionHelper(state, 'importFiles')
  },
  currentUserCanManagePermissions(state) {
    return permissionHelper(state, 'managePermissions')
  },
  currentUserCanManageRoles(state) {
    return permissionHelper(state, 'manageRoles')
  },
  currentUserCanManageUsers(state) {
    return permissionHelper(state, 'manageUsers')
  },
  currentUserCanManageSettings(state) {
    return permissionHelper(state, 'manageSettings')
  },
}

export const actions = {
  // This is automatically run in `src/state/store.js` when the app
  // starts, along with any other actions named `init` in other modules.
  init({ state, dispatch }) {
    setDefaultAuthHeaders(state)
    dispatch('validate')
  },

  // Logs in the current user.
  logIn({ commit, dispatch, getters }, { username, password } = {}) {
    if (getters.loggedIn) return dispatch('validate')

    return axios.post('/api/session', { username, password }).then(response => {
      const user = response.data
      commit('SET_CURRENT_USER', user)
      return user
    })
  },

  // Registers a new user and organization.
  register({ commit, dispatch, getters }, { organization, user } = {}) {
    if (getters.loggedIn) return dispatch('validate')

    return axios
      .post('/api/session/register', { organization, user })
      .then(response => {
        const user = response.data
        commit('SET_CURRENT_USER', user)
        return user
      })
  },

  // Logs out the current user.
  logOut({ commit }) {
    commit('SET_CURRENT_USER', null)
  },

  forgotPassword({ commit }, { emailAddress } = {}) {
    return axios.post('/api/session/forgotPassword', { emailAddress })
  },

  resetPassword(
    { commit },
    { tokenId, oldPassword, newPassword, userId } = {}
  ) {
    return axios.post('/api/session/resetPassword', {
      tokenId,
      oldPassword,
      newPassword,
      userId,
    })
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ commit, state }) {
    if (!state.currentUser) {
      return Promise.resolve(null)
    }

    return axios
      .get('/api/session')
      .then(response => {
        const user = response.data
        commit('SET_CURRENT_USER', user)
        return user
      })
      .catch(error => {
        if (error.response && error.response.status === 401) {
          commit('SET_CURRENT_USER', null)
        }
        return null
      })
  },
}

// ===
// Private helpers
// ===

function getSavedState(key) {
  return JSON.parse(window.localStorage.getItem(key))
}

function saveState(key, state) {
  window.localStorage.setItem(key, JSON.stringify(state))
}

function setDefaultAuthHeaders(state) {
  axios.defaults.headers.common.Authorization = state.currentUser
    ? state.currentUser.token
    : ''
}
